import {
    type AuthClient,
    AuthError,
    type AuthStateListener,
    type AuthUser,
    type LoginCredentials,
    type SignInResult,
} from './AuthClient';

export const SIMULATED_USER: AuthUser = {
    id: 'e2b9c2f4-0000-4000-8000-000000000001',
    firstName: 'Test',
    lastName: 'Test',
    email: 'test@test.test',
    roles: ['shelter-user'],
};

export const SIMULATED_PASSWORD = 'test';

const SESSION_STORAGE_KEY = 'shelter.simulated-auth.v2';

/** Stands in for a refresh token lifetime: how long a session can be restored. */
const SESSION_DURATION_IN_MS = 8 * 60 * 60 * 1000;

/** Stands in for an access token lifetime, renewed while the session is still valid. */
const ACCESS_TOKEN_DURATION_IN_MS = 5 * 60 * 1000;

type StoredSession = {
    user: AuthUser;
    sessionExpiresAt: number;
    accessToken: string;
    accessTokenExpiresAt: number;
};

const isStoredSession = (value: unknown): value is StoredSession => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const candidate = value as Partial<StoredSession>;
    return (
        typeof candidate.accessToken === 'string' &&
        typeof candidate.accessTokenExpiresAt === 'number' &&
        typeof candidate.sessionExpiresAt === 'number' &&
        typeof candidate.user === 'object' &&
        candidate.user !== null &&
        typeof candidate.user.id === 'string'
    );
};

const createAccessToken = () => `simulated-access-token.${crypto.randomUUID()}`;

export type SimulatedAuthClientOptions = {
    /** Injected for deterministic expiry in tests. */
    now?: () => number;
};

/**
 * Temporary authentication client backed by browser storage.
 *
 * It stands in for a real identity provider and therefore imitates the parts that matter for the
 * application: an access token that is renewed on demand, and a session that eventually expires.
 * Nothing outside this file knows how the session is stored.
 */
export class SimulatedAuthClient implements AuthClient {
    private readonly listeners = new Set<AuthStateListener>();

    private readonly now: () => number;

    public constructor(
        private readonly storage: Storage,
        { now = Date.now }: SimulatedAuthClientOptions = {}
    ) {
        this.now = now;
    }

    public async getCurrentUser(): Promise<AuthUser | null> {
        return this.readValidSession()?.user ?? null;
    }

    public async getAccessToken(): Promise<string | null> {
        const session = this.readValidSession();
        if (session === null) {
            return null;
        }

        if (session.accessTokenExpiresAt > this.now()) {
            return session.accessToken;
        }

        // A real client refreshes a token that expired while the session is still valid.
        const renewed: StoredSession = {
            ...session,
            accessToken: createAccessToken(),
            accessTokenExpiresAt: this.now() + ACCESS_TOKEN_DURATION_IN_MS,
        };
        this.writeSession(renewed);

        return renewed.accessToken;
    }

    public async signIn(credentials?: LoginCredentials): Promise<SignInResult> {
        if (credentials === undefined) {
            // A hosted provider would answer `redirecting` here instead.
            throw new AuthError('notSupported', 'The simulated provider requires credentials');
        }

        const isValidEmail = credentials.email.trim().toLowerCase() === SIMULATED_USER.email;
        const isValidPassword = credentials.password === SIMULATED_PASSWORD;
        if (!isValidEmail || !isValidPassword) {
            throw new AuthError('invalidCredentials', 'Unknown email or password');
        }

        this.writeSession({
            user: SIMULATED_USER,
            sessionExpiresAt: this.now() + SESSION_DURATION_IN_MS,
            accessToken: createAccessToken(),
            accessTokenExpiresAt: this.now() + ACCESS_TOKEN_DURATION_IN_MS,
        });

        return { status: 'authenticated', user: SIMULATED_USER };
    }

    public async signOut(): Promise<void> {
        this.storage.removeItem(SESSION_STORAGE_KEY);
    }

    public subscribe(listener: AuthStateListener): () => void {
        this.listeners.add(listener);

        const handleStorageChange = (event: StorageEvent) => {
            if (event.storageArea === this.storage && event.key === SESSION_STORAGE_KEY) {
                listener(this.readValidSession()?.user ?? null);
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            this.listeners.delete(listener);
            window.removeEventListener('storage', handleStorageChange);
        };
    }

    private readValidSession(): StoredSession | null {
        const stored = this.storage.getItem(SESSION_STORAGE_KEY);
        if (stored === null) {
            return null;
        }

        let parsed: unknown;
        try {
            parsed = JSON.parse(stored);
        } catch {
            parsed = null;
        }

        if (!isStoredSession(parsed)) {
            this.storage.removeItem(SESSION_STORAGE_KEY);
            return null;
        }

        if (parsed.sessionExpiresAt <= this.now()) {
            this.storage.removeItem(SESSION_STORAGE_KEY);
            this.notify(null);
            return null;
        }

        return parsed;
    }

    private writeSession(session: StoredSession): void {
        this.storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    }

    private notify(user: AuthUser | null): void {
        for (const listener of this.listeners) {
            listener(user);
        }
    }
}
